import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl, FormCurrencyField, InputAdornment } from 'components/common';
import { IOtherIncomeForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { otherIncomeQuestions } from './questions';
import { useNavigateBack } from 'hooks';
import { useEffect } from 'react';
import { parseApplicationInProgress } from '../helpers';
import { getCurrentRoute } from 'utils/location';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';

export const OtherIncome = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const form = useForm<IOtherIncomeForm>({
    defaultValues: {
      maintenance: '',
      hasMaintenance: null,
      pension: '',
      hasPension: null,
      rentalIncome: '',
      hasRentalIncome: null,
    },
  });
  const formFields = form.watch();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { maintenance, pension, rentalIncome } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        maintenance,
        hasMaintenance: maintenance ? 'true' : 'false',
        pension,
        hasPension: pension ? 'true' : 'false',
        rentalIncome,
        hasRentalIncome: rentalIncome ? 'true' : 'false',
      });
    }
  }, [applicationInProgress]);

  useEffect(() => {
    if (!formFields.hasMaintenance && formFields.maintenance) return form.setValue('maintenance', '');
    if (!formFields.hasPension && formFields.pension) return form.setValue('pension', '');
    if (!formFields.hasRentalIncome && formFields.rentalIncome) return form.setValue('rentalIncome', '');
  }, [form, formFields]);

  const submitFormHandler = async (formData: IOtherIncomeForm) => {
    const { maintenance, pension, rentalIncome } = formData;

    const questions = Object.entries({ maintenance, pension, rentalIncome }).map(([key, answer]) => ({
      question: intl.formatMessage({ id: otherIncomeQuestions[key].labelKey }),
      key,
      answer: answer || 0,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    searchParams.set('section', ApplicationFormPageEnum.BENEFITS_AND_CREDITS);
    setSearchParams(searchParams);
  };

  const isNextButtonDisabled = [formFields.hasMaintenance, formFields.hasPension, formFields.hasRentalIncome].some(
    value => value === '',
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <FormattedMessage id={'applicationForm.OTHER_INCOME.subHeader'} tagName={'h4'} />
        {Object.entries(otherIncomeQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}
            {!formFields[question.booleanQuestionFormKey as keyof IOtherIncomeForm] ||
            formFields[question.booleanQuestionFormKey as keyof IOtherIncomeForm] === 'false' ? (
              <FormControl
                name={question.booleanQuestionFormKey}
                radioOptions={question.options}
                radioGroupProps={{ className: 'mt-2' }}
                onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setValue(question.booleanQuestionFormKey, event.target.value);
                }}
              />
            ) : (
              <FormCurrencyField
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <FormattedMessage id={'common.monthly'} />
                      </InputAdornment>
                    ),
                  },
                }}
                name={key}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
              />
            )}
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isNextButtonDisabled}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
